/** Every fetched value in a store is a `LoadState<T>`: `absent` means the read succeeded and the value does not exist, `error` means the read failed. */
export type LoadState<T> =
  | { status: 'loading' }
  | { status: 'loaded'; value: T }
  | { status: 'error'; message: string }
  | { status: 'absent' }

export function loading<T>(): LoadState<T> {
  return { status: 'loading' }
}

export function loaded<T>(value: T): LoadState<T> {
  return { status: 'loaded', value }
}

export function failed<T>(message: string): LoadState<T> {
  return { status: 'error', message }
}

export function isLoading<T>(state: LoadState<T>): state is { status: 'loading' } {
  return state.status === 'loading'
}

export function isLoaded<T>(state: LoadState<T>): state is { status: 'loaded'; value: T } {
  return state.status === 'loaded'
}

export function isError<T>(state: LoadState<T>): state is { status: 'error'; message: string } {
  return state.status === 'error'
}

export function absent<T>(): LoadState<T> {
  return { status: 'absent' }
}

export function isAbsent<T>(state: LoadState<T>): state is { status: 'absent' } {
  return state.status === 'absent'
}

/** True when the state is `loaded` or `absent`; excludes `error`. */
export function isSettled<T>(state: LoadState<T>): boolean {
  return state.status === 'loaded' || state.status === 'absent'
}
